// Official SARS individual income tax tables — sourced from
// https://www.sars.gov.za/tax-rates/income-tax/rates-of-tax-for-individuals/
// and https://www.sars.gov.za/tax-rates/medical-tax-credit-rates/
// Three-year comparison: 2024/2025, 2025/2026, 2026/2027 tax years.

// Bracket "min" = lower bound of the bracket, "base" = cumulative tax owed
// at that lower bound. tax(income) = base + rate * (income - min) for the
// highest bracket where min <= income.
const BRACKETS_2025_2026 = [
    { min: 0, rate: 0.18, base: 0 },
    { min: 237100, rate: 0.26, base: 42678 },
    { min: 370500, rate: 0.31, base: 77362 },
    { min: 512800, rate: 0.36, base: 121475 },
    { min: 673000, rate: 0.39, base: 179147 },
    { min: 857900, rate: 0.41, base: 251258 },
    { min: 1817000, rate: 0.45, base: 644489 },
];

const BRACKETS_2026_2027 = [
    { min: 0, rate: 0.18, base: 0 },
    { min: 245100, rate: 0.26, base: 44118 },
    { min: 383100, rate: 0.31, base: 79998 },
    { min: 530200, rate: 0.36, base: 125599 },
    { min: 695800, rate: 0.39, base: 185215 },
    { min: 887000, rate: 0.41, base: 259783 },
    { min: 1878600, rate: 0.45, base: 666339 },
];

export const TAX_YEARS = [
    {
        key: "2024-2025",
        label: "2024/2025",
        brackets: BRACKETS_2025_2026, // unchanged from 2024/25 through 2025/26
        rebates: { primary: 17235, secondary: 9444, tertiary: 3145 },
        medicalCredit: { first: 364, second: 728, additional: 246 }, // per month
        retirementCap: 350000,
    },
    {
        key: "2025-2026",
        label: "2025/2026",
        brackets: BRACKETS_2025_2026,
        rebates: { primary: 17235, secondary: 9444, tertiary: 3145 },
        medicalCredit: { first: 364, second: 728, additional: 246 },
        retirementCap: 350000,
    },
    {
        key: "2026-2027",
        label: "2026/2027",
        brackets: BRACKETS_2026_2027,
        rebates: { primary: 17820, secondary: 9765, tertiary: 3249 },
        medicalCredit: { first: 376, second: 752, additional: 254 },
        retirementCap: 430000,
    },
];

export const LATEST_YEAR = TAX_YEARS[TAX_YEARS.length - 1];

export const UIF_CEILING_MONTHLY = 17712;
export const UIF_RATE = 0.01;
export const TRAVEL_ALLOWANCE_TAXABLE_PORTION = 0.8; // default 80% inclusion, no logbook

export const PERIODS = [
    { id: "weekly", label: "Weekly", perYear: 52 },
    { id: "fortnightly", label: "Fortnightly", perYear: 26 },
    { id: "monthly", label: "Monthly", perYear: 12 },
    { id: "annual", label: "Annual", perYear: 1 },
];

export function periodToAnnual(amount, periodId) {
    const p = PERIODS.find((x) => x.id === periodId) || PERIODS[2];
    return (Number(amount) || 0) * p.perYear;
}

export function annualToPeriod(annualAmount, periodId) {
    const p = PERIODS.find((x) => x.id === periodId) || PERIODS[2];
    return (Number(annualAmount) || 0) / p.perYear;
}

function taxFromBrackets(taxableIncome, brackets) {
    if (taxableIncome <= 0) return 0;
    let bracket = brackets[0];
    for (const b of brackets) {
        if (taxableIncome >= b.min) bracket = b;
        else break;
    }
    return bracket.base + bracket.rate * (taxableIncome - bracket.min);
}

function totalRebate(age, rebates) {
    let r = rebates.primary;
    if (age >= 65) r += rebates.secondary;
    if (age >= 75) r += rebates.tertiary;
    return r;
}

function medicalCreditAnnual(members, medicalCredit) {
    const n = Math.max(0, Number(members) || 0);
    if (n <= 0) return 0;
    if (n === 1) return medicalCredit.first * 12;
    if (n === 2) return medicalCredit.second * 12;
    return (medicalCredit.second + (n - 2) * medicalCredit.additional) * 12;
}

/**
 * Computes a full year's tax outcome for a given SARS tax-year table.
 * All money inputs are ANNUAL Rand amounts.
 */
export function computeTaxYear(year, {
    baseAnnualSalary,
    otherIncomeAnnual = 0,
    travelAllowanceAnnual = 0,
    retirementContributionAnnual = 0,
    medicalMembers = 0,
    age,
    uifOptOut = false,
}) {
    const annualCashGross = baseAnnualSalary + otherIncomeAnnual + travelAllowanceAnnual;
    const taxableGrossBasis =
        baseAnnualSalary + otherIncomeAnnual + travelAllowanceAnnual * TRAVEL_ALLOWANCE_TAXABLE_PORTION;

    const retirementDeductible = Math.max(
        0,
        Math.min(retirementContributionAnnual, taxableGrossBasis * 0.275, year.retirementCap)
    );
    const taxableIncome = Math.max(0, taxableGrossBasis - retirementDeductible);

    const taxBeforeRebates = taxFromBrackets(taxableIncome, year.brackets);
    const rebate = totalRebate(age, year.rebates);
    const taxAfterRebates = Math.max(0, taxBeforeRebates - rebate);
    const medCredit = medicalCreditAnnual(medicalMembers, year.medicalCredit);
    const annualTax = Math.max(0, Math.round(taxAfterRebates - medCredit));

    const monthlySalaryForUif = baseAnnualSalary / 12;
    const annualUif = uifOptOut
        ? 0
        : Math.round(Math.min(monthlySalaryForUif, UIF_CEILING_MONTHLY) * UIF_RATE * 12);

    const annualNet = Math.round(annualCashGross - annualTax - annualUif - retirementContributionAnnual);

    return {
        yearLabel: year.label,
        annualGross: Math.round(annualCashGross),
        annualTaxableIncome: Math.round(taxableIncome),
        annualTax,
        annualUif,
        annualNet,
        effectiveRate: annualCashGross > 0 ? (annualTax / annualCashGross) * 100 : 0,
    };
}
